import type { TranslateFunction } from '../types/i18n'
import mapValues from 'lodash/mapValues'
import type { AxiosError } from 'axios'
import type { ValidationError, Error as ApiError } from '@/generated/types'

const API_ERROR_CODES = {
  INVALID_PASSWORD: 'invalid_password',
  EMAIL_NOT_FOUND: 'email_not_found',
  COUPON_ALREADY_REDEEMED: 'coupon_already_redeemed',
  CAMPAIGN_ALREADY_REDEEMED: 'campaign_already_redeemed',
  COUPON_EXPIRED: 'coupon_expired',
  COUPON_INACTIVE: 'coupon_inactive',
  COUPON_REQUIRES_NO_ORDERS: 'coupon_requires_no_orders',
} as const

export function parseResponseErrors(
  errorResponse: AxiosError<ApiError | ValidationError | any>,
  t: TranslateFunction,
) {
  let errors: Record<string, string> = {}

  if (errorResponse.response?.data) {
    const data = errorResponse.response.data

    // Handle specific error codes
    if (data.error === API_ERROR_CODES.INVALID_PASSWORD) {
      errors = { password: t('validation.invalidPassword') }
    } else if (data.error === API_ERROR_CODES.EMAIL_NOT_FOUND) {
      errors = { email: t('validation.emailNotFound') }
    } else if (data.error === API_ERROR_CODES.COUPON_ALREADY_REDEEMED) {
      errors = { code: t('addBalancePage.couponRedemption.errors.alreadyRedeemed') }
    } else if (data.error === API_ERROR_CODES.CAMPAIGN_ALREADY_REDEEMED) {
      errors = { code: t('addBalancePage.couponRedemption.errors.campaignRedeemed') }
    } else if (data.error === API_ERROR_CODES.COUPON_EXPIRED) {
      errors = { code: t('addBalancePage.couponRedemption.errors.expired') }
    } else if (data.error === API_ERROR_CODES.COUPON_INACTIVE) {
      errors = { code: t('addBalancePage.couponRedemption.errors.inactive') }
    } else if (data.error === API_ERROR_CODES.COUPON_REQUIRES_NO_ORDERS) {
      errors = { code: t('addBalancePage.couponRedemption.errors.requiresNoOrders') }
    } else if (data.error === 'Coupon not found') {
      errors = { code: t('addBalancePage.couponRedemption.errors.notFound') }
    }

    // Handle validation errors
    if (data.errors) {
      errors = mapValues(data.errors, (value) =>
        Array.isArray(value) ? value.join(', ') : value,
      )
    }
  }

  return errors
}

/** Returns a single user-friendly error string for coupon redemption failures */
export function parseCouponError(error: any, t: TranslateFunction): string {
  const data = error?.response?.data
  if (!data?.error) return t('addBalancePage.couponRedemption.errors.notFound')

  const errorMap: Record<string, string> = {
    [API_ERROR_CODES.COUPON_ALREADY_REDEEMED]: t('addBalancePage.couponRedemption.errors.alreadyRedeemed'),
    [API_ERROR_CODES.CAMPAIGN_ALREADY_REDEEMED]: t('addBalancePage.couponRedemption.errors.campaignRedeemed'),
    [API_ERROR_CODES.COUPON_EXPIRED]: t('addBalancePage.couponRedemption.errors.expired'),
    [API_ERROR_CODES.COUPON_INACTIVE]: t('addBalancePage.couponRedemption.errors.inactive'),
    [API_ERROR_CODES.COUPON_REQUIRES_NO_ORDERS]: t('addBalancePage.couponRedemption.errors.requiresNoOrders'),
    'Coupon not found': t('addBalancePage.couponRedemption.errors.notFound'),
  }

  return errorMap[data.error] ?? t('addBalancePage.couponRedemption.errors.notFound')
}
